import { KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import Chip from '@mui/material/Chip'
import { MentionPurpose, MentionType } from '@common/interfaces/mentions'
import cn from 'classnames'
import AimStatusEmoji from '../../../components/Common/AimStatus'
import BotBadge from '../../../components/Common/BotBadge'
import InputLabel from '../../../components/Common/InputLabel'
import { IMentionSuggestion } from '../../../components/Common/Mentions'
import { mentionPhotoSubject } from '../../../components/Common/Mentions/mentionHelpers'
import ProfilePhoto from '../../../components/Common/ProfilePhoto'
import TextField from '../../../components/Common/TextField'
import UserPresenceAvatar from '../../../components/Common/UserPresenceAvatar'
import styles from '../Messages.module.scss'

interface IProps {
  label: string
  /** Label beside the control rather than above it. */
  isInline?: boolean
  placeholder?: string
  value: IMentionSuggestion[]
  onChange: (users: IMentionSuggestion[]) => void
  /**
   * Take the caret on mount — only for a surface that exists to be typed into
   * here. Create channel and channel settings open on their own first field.
   */
  autoFocus?: boolean
  /** Already in the channel — dropped from the list, since they can't be added twice. */
  excludeUserIds?: string[]
  /** What the search box starts with — `/add <name>` hands the name over. */
  initialInputValue?: string
  /** Enter with nothing left to search: commit the people already picked. */
  onSubmit?: () => void
  /** Close the list after each pick — for a field with its commit button directly beneath. */
  closeOnSelect?: boolean
  listMentionSuggestions: (params: {
    name?: string
    purpose?: MentionPurpose
  }) => Promise<{ data: IMentionSuggestion[] }>
  /** Also offer CRM contacts, for a picker of people outside the firm (meeting guests). */
  includeContacts?: boolean
  /** Offer a typed address that matches nobody, so anyone can be invited by email. */
  allowEmails?: boolean
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** A typed address as a pickable person; `email:` keeps its id clear of real ones. */
export const emailOnlySuggestion = (email: string): IMentionSuggestion => ({
  id: `email:${email.toLowerCase()}`,
  name: email,
  email,
})

/**
 * Pick internal users.
 *
 * Reuses `listMentionSuggestions` rather than adding a users endpoint: it
 * already resolves exactly the audience team messaging is for, already filters
 * by purpose, and is already permission-checked. Filtering to InternalUser here
 * drops the clients and entities that the same resolver also returns.
 */
const UserPicker = ({
  label,
  isInline = false,
  placeholder,
  value,
  onChange,
  autoFocus = false,
  excludeUserIds = [],
  listMentionSuggestions,
  initialInputValue = '',
  onSubmit,
  closeOnSelect = false,
  includeContacts = false,
  allowEmails = false,
}: IProps) => {
  const [options, setOptions] = useState<IMentionSuggestion[]>([])
  const [inputValue, setInputValue] = useState(initialInputValue)
  // MUI resets a `multiple` field's text on mount, which would wipe the name `/add`
  // handed over; that reset is ignored until the field is first typed in or picked from.
  const isSeedPendingRef = useRef(!!initialInputValue)
  const [isLoading, setIsLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  /**
   * The exclusion list as a primitive, and the fetch keys off this rather than off
   * the array.
   *
   * Depending on the array's identity meant the fetch effect re-ran on every
   * render and never settled: `excludeUserIds` defaults to a fresh `[]` when the
   * prop is omitted (New message, New channel), and ChannelSettingsModal passes an
   * inline `channel.members.map(...)`. Either way it's a new reference each render,
   * so the effect re-ran, `setIsLoading(true)` re-rendered, and the picker sat in a
   * loop firing one suggestions request per debounce for as long as it was open.
   */
  const excludedKey = excludeUserIds.join('|')

  useEffect(() => {
    let isCurrent = true
    setIsLoading(true)

    const excluded = new Set(excludedKey ? excludedKey.split('|') : [])

    // Debounced so typing a name isn't a request per keystroke.
    const timer = setTimeout(() => {
      void listMentionSuggestions({ name: inputValue || undefined, purpose: 'link' })
        .then(({ data }) => {
          if (!isCurrent) return
          setOptions(
            (data ?? []).filter(
              (option) =>
                // Non-user mentions (clients, entities) name nobody to add.
                (!option.type ||
                  option.type === MentionType.InternalUser ||
                  (includeContacts && option.type === MentionType.Contact)) &&
                !excluded.has(option.id),
            ),
          )
        })
        .finally(() => {
          if (isCurrent) setIsLoading(false)
        })
    }, 250)

    return () => {
      isCurrent = false
      clearTimeout(timer)
    }
  }, [inputValue, excludedKey, listMentionSuggestions, includeContacts])

  /**
   * Already-picked people, dropped here rather than through MUI's
   * `filterSelectedOptions` — which would filter *after* the array Tab below reads
   * its fallback highlight out of. Selecting a name a `multiple` Autocomplete
   * already holds removes it, so an unfiltered list makes the top match a chip
   * that deletes itself.
   */
  const availableOptions = useMemo(() => {
    const unpicked = options.filter((option) => !value.some((picked) => picked.id === option.id))
    const typed = inputValue.trim()
    const isNewAddress =
      allowEmails &&
      EMAIL_PATTERN.test(typed) &&
      ![...options, ...value].some((option) => option.email?.toLowerCase() === typed.toLowerCase())
    return isNewAddress ? [emailOnlySuggestion(typed), ...unpicked] : unpicked
  }, [options, value, inputValue, allowEmails])

  /** The row MUI reported as highlighted. A ref: only the key handler reads it. */
  const highlightedOptionRef = useRef<IMentionSuggestion | null>(null)

  const handleChange = useCallback(
    (_event: unknown, next: IMentionSuggestion[]) => {
      isSeedPendingRef.current = false
      onChange(next)
    },
    [onChange],
  )

  /**
   * Tab takes the highlighted name and leaves the caret where it is, so the next
   * recipient can be typed straight away.
   *
   * MUI has no `Tab` case at all — it is a plain blur, and `clearOnBlur` then
   * discards what was typed — so the pick is made here. The highlight has to be
   * reconstructed rather than read: `onHighlightChange` fires only for
   * mouse/keyboard/touch reasons, so `autoHighlight`'s own top-match highlight —
   * the one Enter takes — is never reported, and the head of `availableOptions`
   * stands in for it. With nothing typed and nothing deliberately highlighted
   * there is nobody to pick, and Tab stays the way out of the field.
   *
   * Under `closeOnSelect` the focus move goes ahead, which closes the list and
   * lands on the commit button beneath.
   */
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement> & { defaultMuiPrevented?: boolean }) => {
      if (event.key === 'Enter' && onSubmit && value.length > 0 && !inputValue.trim()) {
        event.preventDefault()
        event.defaultMuiPrevented = true
        onSubmit()
        return
      }
      const isPlainTab =
        event.key === 'Tab' && !event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey
      if (!isPlainTab || !isOpen) return

      const remembered = highlightedOptionRef.current
      const highlighted =
        // Re-found rather than trusted: a refetch can retire the remembered row.
        availableOptions.find((option) => option.id === remembered?.id) ??
        (inputValue.trim() ? availableOptions[0] : undefined)
      if (!highlighted) return

      if (!closeOnSelect) event.preventDefault()
      onChange([...value, highlighted])
      // The typed text named the person now sitting in a chip. MUI clears it
      // itself on the paths it owns; this one is ours.
      setInputValue('')
      highlightedOptionRef.current = null
    },
    [isOpen, inputValue, availableOptions, onChange, value, onSubmit, closeOnSelect],
  )

  return (
    <div className={cn(styles.modalField, { [styles.modalFieldInline]: isInline })}>
      {/* Label above the control rather than MUI's floating variant, matching
          every other form in the portal. The Autocomplete's own input therefore
          carries only a placeholder. */}
      <InputLabel>{label}</InputLabel>
      <Autocomplete
        multiple
        disableCloseOnSelect={!closeOnSelect}
        // The top match is highlighted as you type, so Enter and Tab both land on
        // the name the list is pointing at. Only once something is typed: on the
        // unfiltered list a bare Enter would commit whoever happens to sort first.
        autoHighlight={!!inputValue.trim()}
        // A name handed over by `/add` should show its matches without a keystroke.
        openOnFocus={!!initialInputValue}
        options={availableOptions}
        value={value}
        loading={isLoading}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        // Observed, not controlled: MUI routes every open and close through these,
        // so the key handler can tell whether there is a list to pick from.
        onOpen={() => setIsOpen(true)}
        onClose={() => {
          setIsOpen(false)
          // MUI re-highlights from scratch on reopen; so does this.
          highlightedOptionRef.current = null
        }}
        onHighlightChange={(_event, option) => {
          highlightedOptionRef.current = option
        }}
        inputValue={inputValue}
        onInputChange={(_event, next, reason) => {
          if (reason === 'reset' && isSeedPendingRef.current) return
          isSeedPendingRef.current = false
          setInputValue(next)
          // A new query re-highlights the list from its top, and does it without
          // reporting the change; so does this.
          highlightedOptionRef.current = null
        }}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, selected) => option.id === selected.id}
        // The list is already server-filtered by `name`; filtering again locally
        // would hide results the server deliberately returned.
        filterOptions={(all) => all}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            {/*
             * Keyed by id, not by the suggestion's `avatar` — that field is the mention
             * icon key ('user'), which as an <img src> is just a broken request.
             *
             * Carries presence and status: this is a picker for who to start a
             * conversation with, the same moment-of-decision as the assignee dropdown.
             */}
            {option.type === MentionType.InternalUser || !includeContacts ? (
              <UserPresenceAvatar
                userId={option.id}
                name={option.name}
                className={styles.pickerAvatar}
              />
            ) : (
              <ProfilePhoto
                name={option.name}
                subjectType={mentionPhotoSubject(option.type) ?? undefined}
                id={option.type ? option.id : undefined}
                size="small"
                className={styles.pickerAvatar}
              />
            )}
            <span className={styles.pickerName}>
              {option.name}
              {includeContacts && option.email && option.email !== option.name && (
                <span className={styles.pickerEmail}>{option.email}</span>
              )}
            </span>
            {option.isBot && <BotBadge />}
            {option.type === MentionType.InternalUser && <AimStatusEmoji userId={option.id} />}
          </li>
        )}
        renderTags={(selected, getTagProps) =>
          selected.map((option, index) => {
            const { key, onDelete, ...tagProps } = getTagProps({ index })
            return (
              <Chip
                key={key}
                size="small"
                label={option.name}
                avatar={
                  includeContacts ? (
                    <ProfilePhoto
                      name={option.name}
                      subjectType={mentionPhotoSubject(option.type) ?? undefined}
                      id={option.type ? option.id : undefined}
                      size="xsmall"
                    />
                  ) : undefined
                }
                // Chip stops the click reaching the Autocomplete root, whose handler
                // is what normally puts the caret back in the input.
                onDelete={(event) => {
                  onDelete?.(event)
                  inputRef.current?.focus()
                }}
                {...tagProps}
              />
            )
          })
        }
        // The common TextField, not MUI's: it renders variant="standard" with
        // token-driven borders, where MUI's default outlined variant draws its
        // notch from the frozen light palette and disappears in dark mode.
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={inputRef}
            autoFocus={autoFocus}
            placeholder={placeholder}
            useFinalForm={false}
            fullWidth
          />
        )}
      />
    </div>
  )
}

export default UserPicker
